/**
 * SaveStatusIndicator - Reusable component for displaying save status
 * Extracted from ResponseComposer to promote reusability
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HStack, Text, Spinner, Icon, Pressable,
} from '@gluestack-ui/themed';
import { Check, AlertCircle, RefreshCw } from 'lucide-react-native';

import { formatSaveStatus } from '../utils/autosave';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface SaveStatusIndicatorProps {
  /** Current save status */
  status: SaveStatus;
  /** Timestamp of last successful save */
  lastSavedAt?: string | number | Date | null;
  /** Error message for error state */
  errorMessage?: string | null;
  /** Whether there are unsaved changes */
  isDirty?: boolean;
  /** Callback to retry saving */
  onRetry?: () => void;
  /** Test identifier */
  testID?: string;
}

/**
 * Component for displaying save status with appropriate icons and text
 */
const SaveStatusIndicator: React.FC<SaveStatusIndicatorProps> = React.memo(({
  status,
  lastSavedAt,
  errorMessage,
  isDirty,
  onRetry,
  testID = 'save-status',
}) => {
  const { t } = useTranslation();
  const [, forceUpdate] = useState(0);

  // Update time display every minute for "saved X mins ago"
  useEffect(() => {
    if (status === 'saved') {
      const interval = setInterval(() => {
        forceUpdate((prev) => prev + 1);
      }, 60000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [status]);

  const renderStatus = () => {
    const {
      type, message, showSpinner, showCheckmark, showError,
    } = formatSaveStatus(
      status,
      lastSavedAt ? new Date(lastSavedAt) : null,
      errorMessage,
    );

    // Override 'saved' or 'idle' with 'unsaved' if dirty
    // We only show 'unsaved' if we're not currently saving or in an error state
    if (isDirty && type !== 'saving' && type !== 'error') {
      return (
        <Text size="2xs" color="$textHint" testID={`${testID}-unsaved`}>
          {t('student.task.composer.status.unsaved')}
        </Text>
      );
    }

    if (type === 'idle' || !message) {
      return null;
    }

    // Use translation key for "Saved" if available
    const displayMessage = type === 'saved' && message === 'Saved just now'
      ? t('student.task.composer.status.saved')
      : message;

    return (
      <HStack space="xs" alignItems="center" testID={`${testID}-${type}`}>
        {!!showSpinner && <Spinner size="small" />}
        {!!showCheckmark && <Icon as={Check} size="2xs" color="$green600" />}
        {!!showError && (
          <Pressable onPress={onRetry} hitSlop={10}>
            <HStack space="xs" alignItems="center">
              <Icon as={AlertCircle} size="2xs" color="$red600" />
              <Text size="2xs" color="$red600">
                {displayMessage}
              </Text>
              {!!onRetry && (
                <HStack space="xs" alignItems="center" ml="$1" borderLeftWidth={1} borderColor="$borderLight" pl="$2">
                  <Icon as={RefreshCw} size="xs" color="$primary600" />
                  <Text size="2xs" color="$primary600" fontWeight="$bold">
                    {t('common.buttons.retry')}
                  </Text>
                </HStack>
              )}
            </HStack>
          </Pressable>
        )}
        {!showError && (
          <Text size="2xs" color="$textHint">
            {displayMessage}
          </Text>
        )}
      </HStack>
    );
  };

  return renderStatus();
});

export default SaveStatusIndicator;
